import React from 'react';
import GenericAppContainer from '@modules/ui/pages/GenericAppContainer';
import { StaticPageProps } from '@modules/utilities/pageBuild/types/PageProps';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import SlugsPageParams from '@modules/utilities/pageBuild/types/SlugsPageParams';
import TranslationNamespace from '@modules/localization/enums/TranslationNamespace';
import {
  loadTranslationResources,
  loadPageLayoutTranslationResources,
} from '@modules/localization/utils/translationResourceUtils';
import { stringToLocale } from '@modules/localization/utils/localeUtils';
import { defaultLocale } from '@modules/localization/constants/translationResourceConstants';
import { getLocaleInfoForLocale } from '@modules/localization/implementations/translationResourceHelpers';
import {
  landingPageMaxWidth,
  nonLandingPageMaxWidth,
} from '@modules/layout/layout/constants/layoutConstants';
import { TDocumentationMenuItem } from '@rbx/docsite-utilities';
import Breadcrumb from '@modules/utilities/pageBuild/types/Breadcrumb';
import { updateReferences } from '@modules/apiReference/page/sharedUI/utils/linkableTypeReferenceUtils';
import { setPageTrackingProps } from '@modules/eventStream/utils/pageTrackingUtils';
import { NavigationTypeRaw } from '@modules/utilities/pageBuild/types/NavigationRaw';
import { getAllPaths } from '@modules/utilities/pageBuild/getStaticPathSlugs';
import getPageData from '@modules/utilities/pageBuild/getPageData';

export const getStaticPaths: GetStaticPaths<SlugsPageParams> = async () => {
  return {
    paths: process.env.isExport ? await getAllPaths() : [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<StaticPageProps, SlugsPageParams> = async ({
  params,
}) => {
  const { slugs: rawSlugs } = params ?? { slugs: [] };
  const slugs = rawSlugs ?? [];
  let locale: ReturnType<typeof stringToLocale> = stringToLocale(slugs[0]);
  let isLocalePrefixedPage = true;
  if (!locale) {
    locale = defaultLocale;
    slugs.unshift(locale.toLowerCase());
    isLocalePrefixedPage = false;
  }
  const localeInfo = getLocaleInfoForLocale(locale);

  try {
    const pageData = await getPageData(slugs);

    updateReferences(pageData.data.references);

    const translations: TranslationNamespace[] = [
      TranslationNamespace.OpenCloud,
      TranslationNamespace.ScopeSystem,
      TranslationNamespace.TagDescription,
      TranslationNamespace.TagLabel,
      TranslationNamespace.Article,
      TranslationNamespace.Home,
      TranslationNamespace.CreatorDashboardHome,
      TranslationNamespace.DashboardError,
      TranslationNamespace.NotApproved,
      TranslationNamespace.Moderation,
      TranslationNamespace.AppealsPortal,
      TranslationNamespace.DashboardModeration,
      TranslationNamespace.CreatorDashboardNavigation,
      TranslationNamespace.CreatorDashboardControls,
      TranslationNamespace.CreatorDashboardFeatures,
      TranslationNamespace.CreatorDashboardAssetTypes,
      TranslationNamespace.Courses,
      TranslationNamespace.Assistant,
    ];

    const translationResources = (
      await Promise.all([
        loadTranslationResources(translations, locale),
        loadPageLayoutTranslationResources(locale),
      ])
    ).flat();

    const menuItem = pageData.menuData.find(
      (item: TDocumentationMenuItem & { selected?: boolean }) => item.selected,
    );
    const subMenuItem = menuItem?.items.find(
      (item: TDocumentationMenuItem & { selected?: boolean }) => item.selected,
    );

    const breadcrumbs: Breadcrumb[] = [];
    if (menuItem) {
      breadcrumbs.push({ label: menuItem.title, path: menuItem.path });
    }
    if (subMenuItem) {
      breadcrumbs.push({ label: subMenuItem.title, path: subMenuItem.path });
    }
    const isLandingPage = pageData.isLandingPage || pageData.data.template === 'landing-page';

    const staticPageProps: StaticPageProps = {
      ...pageData,
      blockSearchIndexing: isLocalePrefixedPage && pageData.blockSearchIndexing,
      translationResources,
      breadcrumbs,
      urlBasePath: '/',
      pageMaxWidth: isLandingPage ? landingPageMaxWidth : nonLandingPageMaxWidth,
      localeInfo,
    };
    const pagePropsWithTracking = setPageTrackingProps(
      staticPageProps,
      pageData.data.title ?? pageData.data.openGraphMeta?.title ?? '',
      pageData.data.type ?? NavigationTypeRaw.Markdown,
    );

    const additionalStaticProps = process.env.isExport ? {} : { revalidate: 300 };

    return {
      props: pagePropsWithTracking,
      ...additionalStaticProps,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error in getStaticProps', e);
    return {
      notFound: true,
    };
  }
};

function GenericPage({
  data,
  isLandingPage: pageIsLandingPage,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  updateReferences(data.references);
  return (
    <GenericAppContainer
      data={data}
      isLandingPage={pageIsLandingPage || data.template === 'landing-page'}
    />
  );
}

export default GenericPage;
